import React, { useState, useEffect, useRef } from 'react';
import { db, auth } from '../firebaseConfig';
import { collection, addDoc, query, where, getDocs, deleteDoc, doc } from "firebase/firestore";
import AppBar from './AppBar';
import { generateSummary } from '../utils/embeddings';

function Feed() {
  const [rssLink, setRssLink] = useState('');
  const [articlesByUrl, setArticlesByUrl] = useState({});
  const notesCollectionRef = collection(db, "notes");
  const rssLinksCollectionRef = collection(db, "rssLinks"); 
  const [loading, setLoading] = useState(false);
  const [savedLinks, setSavedLinks] = useState([]);
  const scrollContainerRef = useRef(null);
  const [showLinksPopup, setShowLinksPopup] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);
  const [newRssUrl, setNewRssUrl] = useState('');
  const addFormRef = useRef(null);
  const [feedErrors, setFeedErrors] = useState({});
  const [summarizingArticles, setSummarizingArticles] = useState(new Set());
  const [summaryErrors, setSummaryErrors] = useState({});

  const saveRSSLink = async (url) => {
    if (!url) {
      setFeedErrors(prev => ({...prev, [url]: "Please enter an RSS feed URL"}));
      return false;
    }

    try {
      await addDoc(rssLinksCollectionRef, {
        url: url,
        uid: auth.currentUser.uid,
        timestamp: new Date()
      });
      
      const q = query(rssLinksCollectionRef, where("uid", "==", auth.currentUser.uid));
      const querySnapshot = await getDocs(q);
      const links = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setSavedLinks(links);
      
      setFeedErrors(prev => {
        const newErrors = {...prev};
        delete newErrors[url];
        return newErrors;
      });
      
      return true;
    } catch (error) {
      setFeedErrors(prev => ({...prev, [url]: "Failed to save RSS link"}));
      return false;
    }
  };

  const fetchFeedContent = async (url) => {
    setLoading(true);
    try {
      const response = await fetch(`https://api.rss2json.com/v1/api.json?rss_url=${encodeURIComponent(url)}`);
      const data = await response.json();
      
      if (data.status === 'ok') {
        // Remove the automatic summary generation
        const articles = data.items.map(item => ({
          ...item,
          feedUrl: url, // Add feedUrl to help with updates later
          feedTitle: data.feed.title,
          feedImage: data.feed.image || data.feed.favicon || getFaviconUrl(data.feed.link)
        }));

        setArticlesByUrl(prev => ({
          ...prev,
          [url]: articles
        }));
        
        setFeedErrors(prev => {
          const newErrors = {...prev};
          delete newErrors[url];
          return newErrors;
        });
      } else {
        throw new Error(`Failed to fetch RSS feed`);
      }
    } catch (error) {
      setFeedErrors(prev => ({...prev, [url]: "Failed to fetch feed"}));
    } finally {
      setLoading(false);
    }
  };

  // Helper function to get favicon from site URL
  const getFaviconUrl = (siteUrl) => {
    try {
      const url = new URL(siteUrl);
      return `https://www.google.com/s2/favicons?domain=${url.hostname}&sz=128`;
    } catch (e) {
      return null;
    }
  };

  const handleFetchFeed = async () => {
    const saveSuccess = await saveRSSLink(rssLink);
    if (saveSuccess) {
      await fetchFeedContent(rssLink);
      setRssLink('');
    }
  };

  const saveToNotes = async (title, content, link) => {
    try {
      await addDoc(notesCollectionRef, { 
        text: `${title}\n${link}\n\n${content}`, 
        timestamp: new Date(), 
        uid: auth.currentUser.uid,
        embedding: [] 
      });
      alert("Article saved to notes!");
    } catch (error) {
      console.error("Error saving to notes:", error);
    }
  };

  const handleDeleteFeed = async (feedId, e) => {
    e.stopPropagation(); // Prevent popup from closing
    try {
      await deleteDoc(doc(db, "rssLinks", feedId));
      setSavedLinks(prev => prev.filter(link => link.id !== feedId));
      setArticlesByUrl(prev => {
        const newState = { ...prev };
        delete newState[feedId];
        return newState;
      });
    } catch (error) {
      console.error("Error deleting feed:", error);
    }
  };

  useEffect(() => {
    const fetchSavedLinks = async () => {
      try {
        const q = query(rssLinksCollectionRef, where("uid", "==", auth.currentUser.uid));
        const querySnapshot = await getDocs(q);
        const links = querySnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        setSavedLinks(links);

        for (const link of links) {
          await fetchFeedContent(link.url);
        }
      } catch (error) {
        console.error("Error fetching saved links:", error);
      }
    };

    fetchSavedLinks();
  }, []);

  // Add click outside handler for the form
  useEffect(() => {
    function handleClickOutside(event) {
      if (addFormRef.current && !addFormRef.current.contains(event.target)) {
        setShowAddForm(false);
        setNewRssUrl('');
      }
    }

    if (showAddForm) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showAddForm]);

  const handleAddSource = async (e) => {
    e.preventDefault();
    if (!newRssUrl.trim()) return;

    try {
      const saveSuccess = await saveRSSLink(newRssUrl);
      if (saveSuccess) {
        await fetchFeedContent(newRssUrl);
        setNewRssUrl('');
        setShowAddForm(false);
      }
    } catch (error) {
      console.error("Error adding feed:", error);
      alert("Failed to add feed: " + error.message);
    }
  };

  const getAllArticlesSorted = () => {
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 10);

    const allArticles = Object.values(articlesByUrl)
      .flat()
      .map(article => ({
        ...article,
        pubDate: new Date(article.pubDate)
      }))
      .filter(article => article.pubDate >= thirtyDaysAgo)
      .sort((a, b) => b.pubDate - a.pubDate);
    
    return allArticles;
  };

  const handleGenerateSummary = async (article) => {
    const articleId = article.link || article.guid;
    
    // Don't generate if already summarizing
    if (summarizingArticles.has(articleId)) return;

    setSummarizingArticles(prev => new Set([...prev, articleId]));
    setSummaryErrors(prev => ({ ...prev, [articleId]: null }));

    try {
      // Regular article summary
      const summary = await generateSummary(article.content || article.description);
      
      // Update the article with the new summary
      setArticlesByUrl(prev => ({
        ...prev,
        [article.feedUrl]: prev[article.feedUrl].map(a => 
          (a.link === article.link || a.guid === article.guid)
            ? { ...a, summary }
            : a
        )
      }));
    } catch (error) {
      console.error('Error generating summary:', error);
      setSummaryErrors(prev => ({
        ...prev,
        [articleId]: error.message || 'Failed to generate summary. Please try again later.'
      }));
    } finally {
      setSummarizingArticles(prev => {
        const next = new Set(prev);
        next.delete(articleId);
        return next;
      });
    }
  };

  return (
    <div className="flex-col h-[100dvh] overflow-hidden">
      <div className="flex bg-gray-50 h-[100dvh]">
        <div className="flex-1 max-w-[440px] border-r border-200 overflow-y-auto bg-white flex flex-col pb-[var(--app-bar-height,120px)]">
          <div ref={scrollContainerRef} className="flex-1 overflow-y-auto">
            <div className="px-6 py-3 border-b border-gray-100">
              <div className="flex justify-between items-center">
                <div className="flex items-center gap-2">
                  <button 
                    onClick={() => setShowLinksPopup(true)}
                    className="text-h3 font-bold text-gray-900 transition-colors"
                  >
                    {savedLinks.length} Source{savedLinks.length !== 1 ? 's' : ''}
                  </button>
                  {Object.keys(feedErrors).length > 0 && (
                    <span className="px-2 py-1 text-xs bg-red-100 text-red-600 rounded-full">
                      {Object.keys(feedErrors).length} error{Object.keys(feedErrors).length !== 1 ? 's' : ''}
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setShowAddForm(true)}
                    className="text-sm text-gray-600 transition-colors flex items-center gap-1"
                  >
                    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M12 6v12m-6-6h12" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                    </svg>
                    Add Source
                  </button>
                </div>
              </div>
              
              {/* Add Source Form */}
              {showAddForm && (
                <form 
                  ref={addFormRef}
                  onSubmit={handleAddSource} 
                  className="mt-3 flex gap-2 items-center"
                >
                  <input
                    type="url"
                    value={newRssUrl}
                    onChange={(e) => setNewRssUrl(e.target.value)}
                    placeholder="Enter RSS feed URL"
                    className="flex-1 text-sm px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:border-gray-400 transition-colors"
                    autoFocus
                  />
                  <button
                    type="submit"
                    className="px-3 py-2 bg-gray-900 text-white text-sm rounded-lg hover:bg-gray-700 transition-colors"
                  >
                    Add
                  </button>
                </form>
              )}
            </div>

            <div>
              {getAllArticlesSorted().map((article, index) => {
                const articleId = article.link || article.guid;
                const isGeneratingSummary = summarizingArticles.has(articleId);
                const summaryError = summaryErrors[articleId];

                return (
                  <div 
                    key={index} 
                    className="relative p-4 pl-6 pr-6 border-t border-t-gray-100 bg-white transition-all"
                  >
                    <div className="flex items-start space-x-3">
                      {/* Source Image */}
                      <div className="flex-shrink-0 w-16 h-16 rounded-lg overflow-hidden">
                        {article.thumbnail ? (
                          <img 
                            src={article.thumbnail} 
                            alt={article.feedTitle || "Feed source"} 
                            className="w-full h-full object-cover"
                            onError={(e) => {
                              e.target.onerror = null;
                              // Try feed image first
                              if (article.feedImage) {
                                e.target.src = article.feedImage;
                              } else {
                                e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(article.feedTitle || "RSS")}&background=random`;
                              }
                            }}
                          />
                        ) : article.feedImage ? (
                          <img 
                            src={article.feedImage} 
                            alt={article.feedTitle || "Feed source"} 
                            className="w-full h-full object-contain bg-white p-4 rounded-lg border border-gray-200"
                            onError={(e) => {
                              e.target.onerror = null;
                              e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(article.feedTitle || "RSS")}&background=random`;
                            }}
                          />
                        ) : (
                          <div 
                            className="w-full h-full bg-gray-200 flex items-center justify-center text-gray-500 text-xs"
                            style={{
                              backgroundImage: `url(https://ui-avatars.com/api/?name=${encodeURIComponent(article.feedTitle || "RSS")}&background=random)`,
                              backgroundSize: 'cover'
                            }}
                          ></div>
                        )}
                      </div>
                      
                      {/* Article Text Content */}
                      <div className="space-y-1 flex-1">
                        <h4 className="font-medium">
                          <a 
                            href={article.link}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-black-600 block overflow-hidden visited:text-gray-400"
                            style={{
                              display: '-webkit-box',
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: 'vertical'
                            }}
                          >
                            {article.title}
                          </a>
                        </h4>
                        <p className="text-xs text-gray-500">
                          {new Date(article.pubDate).getDate()} {new Date(article.pubDate).toLocaleString('default', { month: 'short' })}
                          {article.feedTitle && (
                            <span className="ml-1">• {article.feedTitle}</span>
                          )}
                        </p>
                        
                        {/* Summary Section */}
                        <div className="mt-2">
                          {article.summary ? (
                            // Show summary if it exists
                            <div className="text-sm text-gray-600 leading-normal">
                              <span className="font-medium text-gray-500 block mb-1">AI Summary:</span>
                              <div className="pl-3">
                                {article.summary.split('\n').map((line, i) => {
                                  // Check if line starts with a bullet point or dash
                                  const bulletMatch = line.match(/^[•\-\*]\s*(.+)$/);
                                  if (bulletMatch) {
                                    // If it's a bullet point, render with proper formatting
                                    return (
                                      <div key={i} className="flex items-start gap-2 mb-1">
                                        <span className="text-gray-400">•</span>
                                        <span>{bulletMatch[1]}</span>
                                      </div>
                                    );
                                  } else if (line.trim()) {
                                    // If it's not a bullet point but not empty, render as regular text
                                    return <p key={i} className="mb-1">{line}</p>;
                                  }
                                  return null; // Skip empty lines
                                })}
                              </div>
                            </div>
                          ) : isGeneratingSummary ? (
                            // Show loading state
                            <div className="flex items-center gap-2 text-sm text-gray-500">
                              <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                              </svg>
                              Generating summary...
                            </div>
                          ) : summaryError ? (
                            // Show error with retry button
                            <div className="flex items-center gap-2 text-sm">
                              <span className="text-red-600">{summaryError}</span>
                              <button
                                onClick={() => handleGenerateSummary(article)}
                                className="text-blue-600 hover:text-blue-700 font-medium"
                              >
                                Retry
                              </button>
                            </div>
                          ) : (
                            // Show generate button
                            <button
                              onClick={() => handleGenerateSummary(article)}
                              className="text-sm text-blue-600 hover:text-blue-700 font-medium flex items-center gap-1"
                              disabled={summarizingArticles.has(articleId)}
                            >
                              {summarizingArticles.has(articleId) && (
                                <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                                </svg>
                              )}
                              Generate Summary
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      {/* Links Popup */}
      {showLinksPopup && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-start justify-center pt-20">
          <div className="bg-white rounded-lg shadow-xl max-w-[440px] w-full max-h-[80vh] overflow-hidden flex flex-col">
            <div className="p-4 border-b border-gray-100 flex justify-between items-center">
              <h3 className="text-lg font-bold text-gray-900">
                {savedLinks.length} Sources
              </h3>
              <button
                onClick={() => setShowLinksPopup(false)}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            <div className="p-4 overflow-y-auto flex-1">
              <div className="space-y-2">
                {savedLinks.map((link) => (
                  <div 
                    key={link.id} 
                    className="flex items-center justify-between p-3 bg-gray-50 rounded-lg transition-colors"
                  >
                    <span className="text-sm text-gray-600 break-all pr-4">{link.url}</span>
                    <button
                      onClick={(e) => handleDeleteFeed(link.id, e)}
                      className="text-red-600 flex-shrink-0"
                    >
                      Delete
                    </button>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      <AppBar
        className="fixed bottom-0 left-0 right-0"
        onSubmit={handleFetchFeed}
        isSimilarityPanelVisible={false}
        inputValue={rssLink}
        setInputValue={setRssLink}
        placeholder="Enter RSS feed URL..."
      />
    </div>
  );
}

export default Feed;
